// src/utils/widgetUtils.ts

// ✅ Define strong types
export interface NavItem {
  id: string;
  label: string;
  url?: string;
  isEnabled?: boolean;
  isNav?: boolean; // Added isNav property for navigation filtering
}

export interface WidgetData {
  items?: NavItem[] | any[]; // Allow flexible item types
  [key: string]: unknown;
}

export interface Widget {
  type: string;
  pageType?: string; // Optional field to track which page this widget belongs to
  data?: WidgetData | any; // Allow flexible data structure
  [key: string]: unknown;
}

export interface FilteredWidgets {
  navWidgets: Widget[];
  homeWidgets: Widget[];
  menuWidgetTypes: string[];
}

/**
 * Extracts navigation widgets and home page widgets.
 * - Uses URL-based filtering instead of label-based filtering.
 * - Removes "our-" / "the-" prefixes from URLs (e.g., "/our-story" → "story").
 * - Excludes nav, footer, menu, and URL-matched widgets from home list.
 */
export const getDynamicHomeWidgets = (allWidgets: Widget[]): FilteredWidgets => {
  if (!Array.isArray(allWidgets)) {
    return { navWidgets: [], homeWidgets: [], menuWidgetTypes: [] };
  }

  // ✅ Separate nav widgets
  const navWidgets = allWidgets.filter((w) =>
    w.type?.toLowerCase().includes("nav")
  );

  // ✅ Extract menu widget types only
  const menuWidgetTypes = allWidgets
    .filter(w => w.type?.toLowerCase().includes("menu"))
    .map(w => w.type)
    .filter(Boolean) as string[];

  // ✅ Extract URL patterns from all nav widgets dynamically
  const navUrls: string[] = navWidgets
  .flatMap((navWidget) => 
    navWidget.data?.items?.flatMap((item: any) => {
      const url = item.url?.toLowerCase().replace(/^\//, "").trim();
      if (!url) return [];
      
      // Return both the full URL and the key part
      const patterns = [url];
      
      // Also add version without "our-" or "the-"
      const keyPart = url.replace(/^(our-|the-)/, "");
      if (keyPart !== url) {
        patterns.push(keyPart);
      }
      
      return patterns;
    }) ?? []
  )
  .filter(Boolean);

  // ✅ Filter out nav + footer + menu + URL-matched widgets
  const homeWidgets = allWidgets.filter((widget) => {
    const widgetType = widget.type?.toLowerCase() || "";

    // Always exclude nav widgets
    if (widgetType.includes("nav")) {
      return false;
    }

    // Always exclude footer widgets
    if (widgetType.includes("footer")) {
      return false;
    }

    // Always exclude menu widgets
    if (widgetType.includes("menu")) {
      return false;
    }

    // Check if widget type matches any URL pattern (these should be filtered out)
    const isNavRelated = navUrls.some((urlPath) => {
      return widgetType.includes(urlPath);
    });
    
    return !isNavRelated;
  });
  
  return { navWidgets, homeWidgets, menuWidgetTypes };
};

/**
 * Validates widget structure
 */
export const isValidWidget = (widget: unknown): widget is Widget => {
  return (
    typeof widget === 'object' &&
    widget !== null &&
    'type' in widget &&
    typeof (widget as Widget).type === 'string'
  );
};

/**
 * Safely processes widgets array with validation
 */
export const processWidgetsSafely = (widgets: unknown): Widget[] => {
  if (!Array.isArray(widgets)) {
    return [];
  }
  
  return widgets.filter(isValidWidget);
};

/**
 * Find widget index by type pattern (case-insensitive)
 * @param widgets - Array of widgets
 * @param pattern - String pattern to match in widget type (e.g., 'menu', 'footer')
 * @returns Index of found widget, or -1 if not found
 */
export const findWidgetIndexByPattern = (
  widgets: Widget[],
  pattern: string
): number => {
  if (!widgets || !Array.isArray(widgets)) return -1;
  
  return widgets.findIndex(w => 
    w?.type?.toLowerCase().includes(pattern.toLowerCase())
  );
};

/**
 * Replace widget type while preserving all data
 * @param widget - Original widget object
 * @param newType - New widget type to set
 * @returns New widget object with updated type but same data
 */
export const replaceWidgetType = (
  widget: Widget,
  newType: string
): Widget => {
  if (!widget) return widget;
  
  return {
    ...widget,
    type: newType
  };
};

/**
 * Replace menu widget type in widgets array
 * @param widgets - Array of widgets
 * @param newMenuType - New menu widget type
 * @returns Updated widgets array
 */
export const replaceMenuWidget = (
  widgets: Widget[],
  newMenuType: string
): Widget[] => {
  if (!widgets || !Array.isArray(widgets)) return widgets;
  
  const menuIndex = findWidgetIndexByPattern(widgets, 'menu');
  
  if (menuIndex === -1) return widgets;
  
  const updatedWidgets = [...widgets];
  updatedWidgets[menuIndex] = replaceWidgetType(
    widgets[menuIndex],
    newMenuType
  );
  return updatedWidgets;
};
